import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';
import { faqs } from '@/mocks/faq';

function HeroSection() {
  return (
    <section className="relative w-full h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden" aria-labelledby="hero-title">
      <img
        src="https://readdy.ai/api/search-image?query=Cinematic%20movie%20theater%20atmosphere%20with%20warm%20golden%20stage%20lights%20and%20curtains%2C%20elegant%20dark%20red%20and%20gold%20color%20scheme%2C%20professional%20film%20festival%20venue%2C%20dramatic%20spotlight%20effects%2C%20luxurious%20entertainment%20setting&width=1800&height=900&seq=hero-bg-main&orientation=landscape"
        alt="高清视频在线观看平台"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/60"></div>
      <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 text-center">
        <h1 id="hero-title" className="text-3xl md:text-5xl font-bold text-background-50 font-heading leading-tight">
          高清视频在线观看
        </h1>
        <p className="mt-4 md:mt-6 text-base md:text-lg text-background-200 max-w-[650px] mx-auto leading-relaxed">
          超清视频在线观看不卡顿，高清影视在线畅享清晰画质，流畅播放体验升级。
          每日更新海量国产高清视频内容，精彩不错过。
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/all"
            className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-play-circle-line mr-2"></i>
            立即观看
          </Link>
          <Link
            to="/category/movie"
            className="w-full sm:w-auto px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 rounded-lg font-medium text-sm hover:bg-background-50/30 transition-colors whitespace-nowrap cursor-pointer border border-background-50/30"
          >
            <i className="ri-film-line mr-2"></i>
            热门电影
          </Link>
        </div>
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="py-6 md:py-8 bg-background-50" aria-labelledby="category-title">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <h2 id="category-title" className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-5">
          视频分类
        </h2>
        <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-8 gap-3">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="flex flex-col items-center gap-2 p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                <i className={`text-lg ${
                  cat.slug === 'movie' ? 'ri-film-line' :
                  cat.slug === 'tv' ? 'ri-tv-line' :
                  cat.slug === 'variety' ? 'ri-emotion-laugh-line' :
                  cat.slug === 'anime' ? 'ri-ghost-line' :
                  cat.slug === 'documentary' ? 'ri-earth-line' :
                  cat.slug === 'short' ? 'ri-vidicon-line' :
                  cat.slug === 'music' ? 'ri-music-line' :
                  'ri-play-list-2-line'
                }`}></i>
              </div>
              <span className="text-xs text-foreground-700 whitespace-nowrap">{cat.name}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const featured = videos.filter((v) => v.isFeatured);

  return (
    <section className="py-10 md:py-14 bg-background-100" aria-labelledby="featured-title">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 id="featured-title" className="text-lg md:text-xl font-bold text-foreground-950 font-heading">
            精选推荐
          </h2>
          <Link
            to="/category/all"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            查看更多
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
          {featured.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  const latest = [...videos].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <section className="py-10 md:py-14 bg-background-50" aria-labelledby="latest-title">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 id="latest-title" className="text-lg md:text-xl font-bold text-foreground-950 font-heading">
            最新上线
          </h2>
          <Link
            to="/category/all"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            查看更多
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
          {latest.slice(0, 12).map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </div>
    </section>
  );
}

function HotSection() {
  const hot = videos.filter((v) => v.isHot);

  return (
    <section className="py-10 md:py-14 bg-background-100" aria-labelledby="hot-title">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 id="hot-title" className="text-lg md:text-xl font-bold text-foreground-950 font-heading">
            热门视频
          </h2>
          <Link
            to="/category/all"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            查看更多
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
          {hot.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIds, setOpenIds] = useState<number[]>([]);

  const toggle = (id: number) => {
    setOpenIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <section className="py-10 md:py-14 bg-background-50" aria-labelledby="faq-title">
      <div className="max-w-[900px] mx-auto px-4 md:px-6">
        <h2 id="faq-title" className="text-lg md:text-xl font-bold text-foreground-950 font-heading text-center mb-8">
          常见问题
        </h2>
        <div className="space-y-3">
          {faqs.map((faq) => {
            const isOpen = openIds.includes(faq.id);
            return (
              <article
                key={faq.id}
                className="border border-background-200 rounded-lg overflow-hidden"
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                  onClick={() => toggle(faq.id)}
                  aria-expanded={isOpen}
                >
                  <span className="text-sm font-medium text-foreground-900 pr-4" itemProp="name">
                    {faq.question}
                  </span>
                  <i
                    className={`text-foreground-500 transition-transform duration-200 text-lg ${
                      isOpen ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                  ></i>
                </button>
                {isOpen && (
                  <div
                    className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <span itemProp="text">{faq.answer}</span>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <HeroSection />
        <CategoryNav />
        <FeaturedSection />
        <HotSection />
        <LatestSection />
        <FAQSection />
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '91高清视频在线观看 - 超清视频在线观看不卡顿',
            description: '高清视频在线观看专区，主打高清视频在线观看、国产高清视频，超清视频在线观看不卡顿，高清影视在线畅享清晰画质。',
            url: 'https://www.example.com/',
            isPartOf: {
              '@type': 'WebSite',
              name: '91高清视频',
              url: 'https://www.example.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}